import { isEventStatistikPath, isPublicProfilePath, normalizePathname } from '@/utils/clientPaths';

export const APP_DEEP_LINK_HOST = 'app.sporto.id';

const ALLOWED_HOSTS = new Set([APP_DEEP_LINK_HOST, 'localhost']);

export const EVENT_DEEPLINK_RESERVED_SLUGS = [
  'create',
  'edit',
  'dashboard',
  'checkout',
] as const;

function isReservedEventSlug(slug: string): boolean {
  return (EVENT_DEEPLINK_RESERVED_SLUGS as readonly string[]).includes(slug);
}

export function aliasMabarToEventPath(pathname: string): string {
  const path = normalizePathname(pathname);
  const statistik = /^\/mabar\/([^/]+)\/statistik$/.exec(path);
  if (statistik?.[1]) return `/event/${statistik[1]}/statistik`;
  const match = /^\/mabar\/([^/]+)$/.exec(path);
  if (!match?.[1]) return path;
  return `/event/${match[1]}`;
}

/** Rewrite `/mabar/{slug}` dan `/mabar/{slug}/statistik` ke path `/event`. */
export function aliasMabarToEventHref(pathWithSearch: string): string {
  if (!pathWithSearch.startsWith('/') || pathWithSearch.startsWith('//')) {
    return pathWithSearch;
  }
  const q = pathWithSearch.indexOf('?');
  const pathname = q === -1 ? pathWithSearch : pathWithSearch.slice(0, q);
  const search = q === -1 ? '' : pathWithSearch.slice(q);
  const aliased = aliasMabarToEventPath(pathname);
  if (aliased === normalizePathname(pathname)) {
    return pathWithSearch;
  }
  return `${aliased}${search}`;
}

function isCommunityDetailDeepLink(pathname: string): boolean {
  return /^\/community\/[^/]+$/.test(pathname);
}

function isEventListDeepLink(pathname: string): boolean {
  return pathname === '/event';
}

function isEventDetailDeepLink(pathname: string): boolean {
  const match = /^\/event\/([^/]+)$/.exec(pathname);
  if (!match?.[1]) return false;
  return !isReservedEventSlug(match[1]);
}

function isEventStatistikDeepLink(pathname: string): boolean {
  if (!isEventStatistikPath(pathname)) return false;
  const match = /^\/event\/([^/]+)\/statistik$/.exec(pathname);
  return !!match?.[1] && !isReservedEventSlug(match[1]);
}

export function resolveAppDeepLink(url: string, homePath: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return homePath;
  }

  if (!ALLOWED_HOSTS.has(parsed.hostname)) {
    return homePath;
  }

  const pathname = aliasMabarToEventPath(parsed.pathname);
  if (pathname === '/' || pathname === '') {
    return homePath;
  }

  const allowed =
    isCommunityDetailDeepLink(pathname) ||
    isEventListDeepLink(pathname) ||
    isEventDetailDeepLink(pathname) ||
    isEventStatistikDeepLink(pathname) ||
    isPublicProfilePath(pathname);

  if (!allowed) {
    return homePath;
  }

  return `${pathname}${parsed.search}`;
}

export function isAppDeepLinkHost(hostname: string): boolean {
  return ALLOWED_HOSTS.has(hostname);
}

/** Icon tap / WebView `server.url` — bukan deeplink konten. */
export function isOriginOnlyAppUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (!ALLOWED_HOSTS.has(parsed.hostname)) {
    return false;
  }
  const pathname = aliasMabarToEventPath(parsed.pathname);
  return pathname === '/' || pathname === '';
}

/** Deeplink: home di bawah stack supaya back (in-app + hardware) ke home. */
export function applyDeepLinkNavigation(
  navigate: (to: string, options?: { replace?: boolean }) => void,
  resolved: string,
  homePath: string
): void {
  if (resolved === homePath) {
    navigate(homePath, { replace: true });
    return;
  }
  navigate(homePath, { replace: true });
  navigate(resolved);
}
